import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileSpreadsheet,
  Users,
  Inbox,
  MessageSquareWarning,
  ListChecks,
  Settings as SettingsIcon,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/reports", label: "Reports", icon: FileSpreadsheet, testid: "nav-reports" },
  { to: "/customers", label: "Kunden", icon: Users, testid: "nav-customers" },
  { to: "/audit", label: "Audit", icon: ListChecks, testid: "nav-audit" },
  { to: "/postbox", label: "Postbox", icon: Inbox, testid: "nav-postbox" },
  { to: "/claims", label: "Claims", icon: MessageSquareWarning, testid: "nav-claims" },
  { to: "/settings", label: "Einstellungen", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[#f8f9fa]">
      <aside
        data-testid="app-sidebar"
        className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col"
      >
        <div className="px-6 py-7 border-b border-slate-100">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="sidebar-logo">
            <div className="w-9 h-9 bg-slate-900 flex items-center justify-center rounded-sm">
              <span className="font-display text-white text-base font-bold">G</span>
            </div>
            <div>
              <div className="font-display text-[15px] font-bold text-slate-900 tracking-tight">
                GND Audit
              </div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                Lightspeed Tool
              </div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "is-active" : ""}`
                }
              >
                <Icon size={18} strokeWidth={1.6} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-slate-100">
          <div className="px-3 py-2 mb-2">
            <div className="text-[11px] uppercase tracking-[0.12em] text-slate-400 font-semibold mb-1">
              Angemeldet als
            </div>
            <div className="text-sm font-medium text-slate-900 truncate" data-testid="sidebar-user-name">
              {user?.name || "—"}
            </div>
            <div className="text-xs text-slate-500 truncate" data-testid="sidebar-user-email">
              {user?.email || ""}
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-sm transition-colors"
          >
            <LogOut size={16} strokeWidth={1.6} /> Abmelden
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div key={location.pathname} className="fade-in">
          {children}
        </div>
      </main>
    </div>
  );
}
